import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, Copy, RefreshCw, X } from 'lucide-react';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import {
  loadMonthlyMeetingSites,
  peekMonthlyMeetingSites,
  previousMeetingMonth,
} from './monthly-meeting-read';
import {
  monthlyAgenda,
  monthlyAgendaReader,
  type AgendaReport,
  type AgendaRow,
  type MonthlyAgendaId,
} from './monthly-agenda-read';
import './monthly-agenda-workspace.css';

type Site = { siteId: string; displayName: string };
type Loaded = { site: Site; report: AgendaReport | null; failed: boolean };
export type MonthlyAgendaProps = {
  uid: string;
  siteId: string | null;
  initialMonth: string;
  onClose(): void;
  onOpen(id: string, readSiteId?: string): void;
  featureIds: readonly string[];
  presentation?: 'dialog' | 'page';
};
const text = (value: number | string | null) =>
  value === null ? '—' : typeof value === 'number' ? value.toLocaleString('ko-KR') : value;

export function MonthlyAgendaWorkspace({
  uid,
  siteId,
  initialMonth,
  onClose,
  onOpen,
  presentation = 'page',
}: MonthlyAgendaProps) {
  const [sites, setSites] = useState<readonly Site[]>(() =>
    siteId ? [{ siteId, displayName: '사업장' }] : (peekMonthlyMeetingSites(uid) ?? []),
  );
  const [siteError, setSiteError] = useState(false),
    [selection, setSelection] = useState(siteId ?? 'all');
  const [meetingMonth, setMeetingMonth] = useState(initialMonth),
    [month, setMonth] = useState(() => previousMeetingMonth(initialMonth));
  const [section, setSection] = useState<MonthlyAgendaId>('plan'),
    [revision, setRevision] = useState(0),
    [busy, setBusy] = useState(false),
    [locked, setLocked] = useState(false);
  const [loaded, setLoaded] = useState<{ key: string; rows: Loaded[] } | null>(null),
    [copied, setCopied] = useState('');
  const generation = useRef(0),
    lastRevision = useRef(revision),
    panel = useRef<HTMLElement>(null),
    sessionActive = useRef(true);
  const siteKey = JSON.stringify(
    sites.filter((site) => selection === 'all' || site.siteId === selection),
  );
  const requestKey = JSON.stringify([uid, siteKey, meetingMonth, month, section]);
  const invalidateRequest = useCallback(() => {
    generation.current++;
  }, []);
  useEffect(() => {
    if (siteId) return;
    let active = true;
    void loadMonthlyMeetingSites(uid).then(
      (value) => {
        if (active && sessionActive.current) {
          setSites(value);
          setSiteError(false);
        }
      },
      () => {
        if (active && sessionActive.current) setSiteError(true);
      },
    );
    return () => {
      active = false;
    };
  }, [uid, siteId]);
  useEffect(() => {
    const unsubscribe = onSensitiveMemoryCacheClear(() => {
      sessionActive.current = false;
      generation.current++;
      setLocked(true);
      setLoaded(null);
      setSites([]);
      setBusy(false);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  useEffect(() => {
    const run = ++generation.current;
    if (locked) return;
    const selected = JSON.parse(siteKey) as Site[],
      force = revision !== lastRevision.current;
    lastRevision.current = revision;
    setCopied('');
    const input = (site: Site) => ({ uid, siteId: site.siteId, month, meetingMonth, section });
    const rows: Loaded[] = selected.map((site) => ({
      site,
      report: monthlyAgendaReader.peek(input(site)),
      failed: false,
    }));
    setLoaded({ key: requestKey, rows: [...rows] });
    setBusy(selected.length > 0);
    let cursor = 0;
    void Promise.all(
      Array.from({ length: Math.min(2, selected.length) }, async () => {
        for (;;) {
          const index = cursor++,
            site = selected[index];
          if (!site || generation.current !== run) return;
          try {
            const report = await monthlyAgendaReader.load(input(site), force);
            if (generation.current !== run) return;
            rows[index] = { site, report, failed: false };
          } catch {
            if (generation.current !== run) return;
            rows[index] = { site, report: null, failed: true };
          }
          setLoaded({ key: requestKey, rows: [...rows] });
        }
      }),
    ).finally(() => {
      if (generation.current === run) setBusy(false);
    });
    return invalidateRequest;
  }, [uid, siteKey, meetingMonth, month, section, revision, locked, requestKey, invalidateRequest]);
  useEffect(() => {
    if (presentation !== 'dialog') return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null,
      overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
      if (event.key === 'Tab') {
        const buttons = [
            ...(panel.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled),input,select',
            ) ?? []),
          ],
          first = buttons[0],
          last = buttons.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', key, true);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', key, true);
      previous?.focus();
    };
  }, [presentation, onClose]);
  const rows = loaded?.key === requestKey ? loaded.rows : [],
    first = rows.find((row) => row.report)?.report;
  const title = monthlyAgenda.find((item) => item[0] === section)![1],
    position = monthlyAgenda.findIndex((item) => item[0] === section);
  const open = (row: AgendaRow, site: Site) => {
    onClose();
    onOpen(row.featureId, site.siteId);
  };
  const copy = async () => {
    if (!first || busy || rows.some((row) => row.failed || !row.report)) return;
    const lines = [
      ['사업장', ...first.headings],
      ...rows.flatMap(({ site, report }) =>
        report!.rows.map((row) => [site.displayName, row.label, ...row.cells.map(text)]),
      ),
    ];
    try {
      await navigator.clipboard.writeText(
        lines
          .map((row) => row.map((cell) => String(cell).replace(/[\t\r\n]+/gu, ' ')).join('\t'))
          .join('\n'),
      );
      setCopied('집계표를 복사했습니다.');
    } catch {
      setCopied('복사하지 못했습니다. 표를 직접 선택해 복사해 주세요.');
    }
  };
  const footer =
    first && rows.length > 0 && rows.every((row) => row.report && !row.failed)
      ? first.sumColumns.map((index) => {
          const included = rows.flatMap((row) =>
            row.report!.rows.filter((value) => section !== 'budget' || value.key === 'total'),
          );
          return {
            index,
            value: included.every((row) => typeof row.cells[index] === 'number')
              ? included.reduce((sum, row) => sum + Number(row.cells[index]), 0)
              : null,
          };
        })
      : [];
  const riskTotals =
    section === 'risk' && first
      ? first.rows.map((row) => {
          const sources = rows.map((site) =>
            site.report?.rows.find((value) => value.key === row.key),
          );
          const cells = [0, 1, 2].map((index) =>
            sources.length && sources.every((source) => typeof source?.cells[index] === 'number')
              ? sources.reduce((sum, source) => sum + Number(source!.cells[index]), 0)
              : null,
          );
          return {
            key: row.key,
            label: row.label,
            cells: [
              ...cells,
              typeof cells[0] === 'number' && cells[0] > 0 && typeof cells[1] === 'number'
                ? `${Math.round((cells[1] / cells[0]) * 1000) / 10}%`
                : null,
            ],
          };
        })
      : [];
  const content = (
    <article
      className="hqma"
      ref={panel}
      role={presentation === 'dialog' ? 'dialog' : 'region'}
      aria-modal={presentation === 'dialog' ? true : undefined}
      aria-label="사업장 월회의 집계표"
    >
      <header className="hqma-header">
        <div>
          <span>월회의 진행자료</span>
          <h2>사업장 회의자료</h2>
        </div>
        <button
          type="button"
          className="hqma-icon"
          aria-label="사업장 회의자료 닫기"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </header>
      <div className="hqma-toolbar">
        <label>
          사업장
          <select
            aria-label="월회의 집계 사업장"
            value={selection}
            disabled={Boolean(siteId) || locked}
            onChange={(event) => setSelection(event.target.value)}
          >
            {!siteId && <option value="all">전체 사업장</option>}
            {sites.map((site) => (
              <option key={site.siteId} value={site.siteId}>
                {site.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          회의월
          <input
            aria-label="월회의 회의월"
            type="month"
            value={meetingMonth}
            disabled={locked}
            onChange={(event) => {
              if (event.target.value) {
                setMeetingMonth(event.target.value);
                setMonth(previousMeetingMonth(event.target.value));
              }
            }}
          />
        </label>
        <label>
          실적 기준월
          <input
            aria-label="월회의 실적 기준월"
            type="month"
            value={month}
            disabled={locked}
            onChange={(event) => {
              if (event.target.value) setMonth(event.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className="hqma-refresh"
          aria-label="월회의 집계표 새로고침"
          disabled={busy || locked}
          onClick={() => setRevision((value) => value + 1)}
        >
          <RefreshCw size={15} />
          새로고침
        </button>
      </div>
      <div className="hqma-layout">
        <nav aria-label="월회의 진행 목차">
          {monthlyAgenda.map(([id, label], i) => (
            <button
              type="button"
              key={id}
              aria-current={section === id ? 'step' : undefined}
              onClick={() => setSection(id)}
            >
              <span>{String(i + 1).padStart(2, '0')}</span>
              <strong>{label}</strong>
            </button>
          ))}
        </nav>
        <section className="hqma-content" aria-busy={busy}>
          <div className="hqma-title">
            <div>
              <p>
                {section === 'accident'
                  ? `${meetingMonth} 사고 현황 · ${meetingMonth.slice(0, 4)}년 1월~회의월 누계`
                  : `${month} 실적${section === 'budget' ? ` · ${month.slice(0, 4)}년 1월~기준월 누계` : ''}`}
              </p>
              <h3>
                {position + 1}. {title}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => void copy()}
              disabled={busy || !first || rows.some((row) => row.failed || !row.report)}
            >
              <Copy size={14} />표 복사
            </button>
          </div>
          {locked ? (
            <p role="alert">접속 정보가 변경되었습니다. 다시 접속해 주세요.</p>
          ) : siteError && !sites.length ? (
            <p role="alert">사업장 목록을 불러오지 못했습니다.</p>
          ) : !rows.length ? (
            <p role="status">
              {sites.length ? '조회할 사업장을 선택해 주세요.' : '사업장 목록을 확인하고 있습니다.'}
            </p>
          ) : (
            <>
              {rows.some((row) => row.report?.retained) && (
                <p className="hqma-notice" role="status">
                  최근 확인한 자료를 표시하고 있습니다. 최신 내용은 새로고침으로 확인해 주세요.
                </p>
              )}
              {!first && busy ? (
                <p role="status">사업장 자료를 집계하고 있습니다.</p>
              ) : (
                <div className="hqma-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">사업장</th>
                        {(first?.headings ?? ['자료']).map((heading) => (
                          <th scope="col" key={heading}>
                            {heading}
                          </th>
                        ))}
                        <th scope="col">근거 자료</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ site, report, failed }) =>
                        !report ? (
                          <tr key={site.siteId}>
                            <th scope="row">{site.displayName}</th>
                            <td colSpan={(first?.headings.length ?? 1) + 1}>
                              {failed
                                ? '자료를 확인하지 못했습니다. 새로고침해 주세요.'
                                : '자료 집계 중'}
                            </td>
                          </tr>
                        ) : !report.rows.length ? (
                          <tr key={site.siteId}>
                            <th scope="row">{site.displayName}</th>
                            <td colSpan={report.headings.length}>
                              해당 기간에 등록된{' '}
                              {section === 'education' ? '협력업체 특별교육 일지' : '자료'}가
                              없습니다.
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() =>
                                  open(
                                    {
                                      key: 'source',
                                      label: '',
                                      cells: [],
                                      featureId:
                                        section === 'plan'
                                          ? 'activity-plan'
                                          : section === 'education'
                                            ? 'safety-training'
                                            : section === 'voice'
                                              ? 'meeting-minutes'
                                              : 'incident',
                                    },
                                    site,
                                  )
                                }
                              >
                                자료 보기
                                <ArrowUpRight size={13} />
                              </button>
                            </td>
                          </tr>
                        ) : (
                          report.rows.map((row, i) => (
                            <tr
                              key={`${site.siteId}:${row.key}`}
                              className={row.key === 'total' ? 'hqma-total' : ''}
                            >
                              {i === 0 && (
                                <th scope="rowgroup" rowSpan={report.rows.length}>
                                  {site.displayName}
                                </th>
                              )}
                              <th scope="row">
                                {row.label}
                                {row.note && <small>{row.note}</small>}
                              </th>
                              {row.cells.map((cell, j) => (
                                <td
                                  key={j}
                                  className={typeof cell === 'number' ? 'hqma-number' : ''}
                                >
                                  {typeof cell === 'number' ? (
                                    <button
                                      type="button"
                                      aria-label={`${site.displayName} ${row.label} ${report.headings[j + 1]} ${text(cell)} 근거 보기`}
                                      onClick={() => open(row, site)}
                                    >
                                      {text(cell)}
                                    </button>
                                  ) : (
                                    text(cell)
                                  )}
                                </td>
                              ))}
                              <td>
                                <button
                                  type="button"
                                  className="hqma-source"
                                  onClick={() => open(row, site)}
                                >
                                  자료 보기
                                  <ArrowUpRight size={13} />
                                </button>
                              </td>
                            </tr>
                          ))
                        ),
                      )}
                    </tbody>
                    {riskTotals.length > 0 ? (
                      <tfoot>
                        {riskTotals.map((row) => (
                          <tr key={row.key}>
                            <th colSpan={2}>전체 · {row.label}</th>
                            {row.cells.map((cell, i) => (
                              <td key={i}>{text(cell)}</td>
                            ))}
                            <td />
                          </tr>
                        ))}
                      </tfoot>
                    ) : (
                      footer.length > 0 &&
                      section !== 'voice' && (
                        <tfoot>
                          <tr>
                            <th colSpan={2}>전체 합계</th>
                            {first?.headings.slice(1).map((_, i) => (
                              <td key={i}>
                                {text(footer.find((total) => total.index === i)?.value ?? null)}
                              </td>
                            ))}
                            <td />
                          </tr>
                        </tfoot>
                      )
                    )}
                  </table>
                </div>
              )}
              <div className="hqma-notes">
                {first?.notes.map((note) => (
                  <p key={note}>{note}</p>
                ))}
                {section === 'voice' && (
                  <p>안전신문고의 접수·처리 현황은 기능 연결 후 이 항목에서 집계합니다.</p>
                )}
              </div>
              {copied && <p role="status">{copied}</p>}
            </>
          )}
        </section>
      </div>
      <footer className="hqma-footer">
        <span>
          {busy
            ? '사업장 자료 확인 중'
            : `${rows.filter((row) => row.report).length} / ${rows.length}개 사업장 자료`}
        </span>
        <div>
          <button
            type="button"
            disabled={position === 0}
            onClick={() => setSection(monthlyAgenda[position - 1]![0])}
          >
            <ChevronLeft size={14} />
            이전 항목
          </button>
          <button
            type="button"
            disabled={position + 1 === monthlyAgenda.length}
            onClick={() => setSection(monthlyAgenda[position + 1]![0])}
          >
            다음 항목
            <ChevronRight size={14} />
          </button>
        </div>
      </footer>
    </article>
  );
  return presentation === 'dialog'
    ? createPortal(<div className="hqma-layer">{content}</div>, document.body)
    : content;
}
